import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseApiPath } from '@/app/common/api/config';
import customFetchBase from '@/app/common/utils/customFetchBase';
import type {
  AnalysisContentResponse,
  AnalysisHistoryResponse,
  AnalysisId,
  ReportLanguage,
  AnalysisMutationResponse,
  AnalysisOverviewResponse,
  AnalysisSpeedResponse,
  CruxReportResponse,
  ComparisonResponse,
  CompareRequest,
  CreateAnalysisRequest,
  CreateAnalysisResponse,
  AnalysisServersResponse,
  AnalysisScheduleInput,
  AnalysisScheduleResponse,
  AnalysisPdfDownloadRequest
} from '@/app/PageSpeed/api/types/analysisApiTypes';

const analysisApi = createApi({
  reducerPath: 'analysis',
  tagTypes: ['AnalysisHistory', 'AnalysisSchedule'],
  baseQuery: (...args) => customFetchBase(getBaseApiPath(), ...args),
  endpoints: build => ({
    getAnalysisHistory: build.query<AnalysisHistoryResponse, void | undefined>({
      query: () => ({
        url: `/lighthouse/v1/history`,
        method: 'GET'
      }),
      providesTags: ['AnalysisHistory']
    }),

    getAnalysisServers: build.query<AnalysisServersResponse, void | undefined>({
      query: () => ({
        url: `/lighthouse/v1/servers`,
        method: 'GET'
      })
    }),

    getAnalysisContent: build.query<AnalysisContentResponse, AnalysisId>({
      query: (id: AnalysisId) => ({
        url: `/lighthouse/v1/content`,
        method: 'POST',
        body: { id }
      })
    }),
    getAnalysisSpeed: build.query<
      AnalysisSpeedResponse,
      {
        id?: AnalysisId;
        slug?: string;
        report_lang: ReportLanguage;
        strategy?: 'mobile' | 'desktop';
      }
    >({
      query: ({ id, slug, report_lang, strategy }) => ({
        url: slug ? `/lighthouse/v1/public/report` : `/lighthouse/v1/report`,
        method: 'POST',
        body: slug
          ? { slug, report_lang, strategy }
          : { task_id: id, report_lang, strategy }
      })
    }),
    updateAnalysisVisibility: build.mutation<
      AnalysisMutationResponse,
      { task_id: AnalysisId; is_public: boolean }
    >({
      query: body => ({
        url: `/lighthouse/v1/history/visibility`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['AnalysisHistory']
    }),
    downloadAnalysisPdf: build.mutation<
      Blob,
      AnalysisPdfDownloadRequest
    >({
      query: body => ({
        url: `/lighthouse/v1/history/download-pdf`,
        method: 'POST',
        body,
        responseHandler: (response: Response) => response.blob()
      })
    }),
    getAnalysisCrux: build.query<CruxReportResponse, AnalysisId>({
      query: (id: AnalysisId) => ({
        url: `/lighthouse/v1/field-data/report`,
        method: 'POST',
        body: { task_id: id }
      })
    }),
    getAnalysisOverview: build.query<AnalysisOverviewResponse, AnalysisId>({
      query: (id: AnalysisId) => ({
        url: `/lighthouse/v1/overview`,
        method: 'POST',
        body: { id }
      })
    }),
    createAnalysis: build.mutation<
      CreateAnalysisResponse,
      CreateAnalysisRequest
    >({
      query: ({ body }: CreateAnalysisRequest) => ({
        url: `/lighthouse/v1/create`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['AnalysisHistory']
    }),
    compareAnalysis: build.mutation<ComparisonResponse, CompareRequest>({
      query: body => ({
        url: `/lighthouse/v1/compare`,
        method: 'POST',
        body
      })
    }),
    deleteAnalysis: build.mutation<AnalysisMutationResponse, AnalysisId>({
      query: (id: AnalysisId) => ({
        url: `/lighthouse/v1/delete`,
        method: 'POST',
        body: { id }
      }),
      invalidatesTags: ['AnalysisHistory']
    }),
    pauseAnalysis: build.mutation<AnalysisMutationResponse, AnalysisId>({
      query: (id: AnalysisId) => ({
        url: `/lighthouse/v1/pause`,
        method: 'POST',
        body: { id }
      }),
      invalidatesTags: ['AnalysisHistory']
    }),
    getAnalysisSchedule: build.query<AnalysisScheduleResponse, void>({
      query: () => ({
        url: `/lighthouse/v1/schedule/get`,
        method: 'GET'
      }),
      providesTags: ['AnalysisSchedule']
    }),
    createAnalysisSchedule: build.mutation<
      AnalysisScheduleResponse,
      AnalysisScheduleInput
    >({
      query: body => ({
        url: `/lighthouse/v1/schedule/create`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['AnalysisSchedule']
    }),
    updateAnalysisSchedule: build.mutation<
      AnalysisScheduleResponse,
      AnalysisScheduleInput & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/lighthouse/v1/schedule/update`,
        method: 'POST',
        body: { id, ...body }
      }),
      invalidatesTags: ['AnalysisSchedule']
    })
  })
});

export const {
  useGetAnalysisHistoryQuery,
  useGetAnalysisServersQuery,
  useCreateAnalysisMutation,
  useDeleteAnalysisMutation,
  useGetAnalysisContentQuery,
  useGetAnalysisSpeedQuery,
  useUpdateAnalysisVisibilityMutation,
  useDownloadAnalysisPdfMutation,
  useGetAnalysisCruxQuery,
  useGetAnalysisOverviewQuery,
  useCompareAnalysisMutation,
  usePauseAnalysisMutation,
  useGetAnalysisScheduleQuery,
  useCreateAnalysisScheduleMutation,
  useUpdateAnalysisScheduleMutation
} = analysisApi;
export default analysisApi;
